/**
 * The Dashboard's data layer: how to fetch ONE practice's payload, and how to
 * fold several of them into one as if they were a single practice.
 *
 * Both live here because they are two halves of one contract — the aggregate is
 * literally the fetch run once per practice and then merged, so the shape can
 * only be defined once. DashboardPage and PracticeContext (which prefetches the
 * single-practice payload to warm the same cache entry) both import the fetch
 * from here; that copy used to be duplicated in both files with a comment on
 * each asking the reader to keep them byte-for-byte identical.
 */
import axios from './axiosInstance';
import { countTasksByStatus } from './taskStatusCounts';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * One practice's dashboard payload: the KPI summary, the raw records behind the
 * heatmap and the trend chart, and the task-tile counts.
 *
 * The task fetch is best-effort — it resolves with `taskCounts: null` rather
 * than failing the whole payload, because call statistics are still worth
 * showing without it. Callers that aggregate MUST notice that null: six tiles
 * summed over four practices instead of five look perfectly plausible.
 */
export const fetchPracticeDashboard = async (practiceId, startDate, endDate) => {
  // Call statistics summary - primary source of data
  const statsResponse = await axios.get(
    `${API_BASE_URL}/call-statistics/practice/${practiceId}/summary`,
    { params: { start_date: startDate, end_date: endDate } }
  );

  // Full records for early_hangup deduplication
  const recordsResponse = await axios.get(
    `${API_BASE_URL}/call-statistics/practice/${practiceId}`,
    { params: { start_date: startDate, end_date: endDate } }
  );

  let taskCountsResult = null;
  try {
    // The endpoint filters the range itself (inclusive, as whole Europe/Berlin
    // days). This replaces an over-fetch of every task back to startDate plus
    // a client-side clamp that compared created_at's UTC day against these
    // LOCAL days — which mis-bucketed anything called before 01:00/02:00.
    const tasksResponse = await axios.get(
      `${API_BASE_URL}/tasks/practice/${practiceId}`,
      { params: { start_date: startDate, end_date: endDate } }
    );
    taskCountsResult = countTasksByStatus(tasksResponse.data);
  } catch (error) {
    console.log('Task counts not available:', error.message);
    taskCountsResult = null;
  }

  return {
    callStats: statsResponse.data,
    allCallRecords: recordsResponse.data,
    taskCounts: taskCountsResult,
  };
};

/**
 * Fold several practices' dashboard payloads into ONE, as if they were a single
 * practice.
 *
 * Every number the Dashboard renders is a pooled sum: totals add, breakdowns add
 * per key, raw record lists concatenate. Nothing is averaged — a rate is always
 * re-derived from the summed numerator and the summed denominator (Σautomated /
 * Σtotal), never as the mean of per-practice rates, which would let a practice
 * with three calls weigh as much as one with three hundred.
 *
 * Only the fields DashboardPage actually reads are merged. The summary endpoint
 * also returns `unique_callers` and `average_call_duration_seconds`, which are
 * the two fields that cannot be summed — and neither is rendered, so neither is
 * carried here rather than being carried wrong.
 *
 * The returned object has the same shape the page already consumes, so every
 * downstream getter and useMemo works unchanged.
 */

// A practice with zero calls in the range gets a SHORT summary from the backend
// ({total_calls: 0, date_range, message}) with no breakdown keys at all, so
// every read below has to tolerate a missing key rather than assume the full shape.
const addCounts = (target, source) => {
  Object.entries(source || {}).forEach(([key, value]) => {
    target[key] = (target[key] || 0) + (Number(value) || 0);
  });
};

/**
 * @param parts array of { callStats, allCallRecords, taskCounts } — one per
 *              practice, in scope order. Entries whose fetch failed are expected
 *              to be filtered out by the caller.
 */
export const mergeDashboardData = (parts) => {
  const callStats = {
    total_calls: 0,
    completed_call_duration_seconds: 0,
    call_type_breakdown: {},
    call_reasons_breakdown: {},
    // Concatenated, not merged: buildEscapeRows already folds duplicate keys
    // together and sums their counts, so two practices reporting the same code
    // land on one row without any work here.
    escape_breakdown: [],
    escape_scope_total: 0,
    performance: { automated: 0, forwarded: 0, forwarded_answered: 0, open: 0, total: 0 },
  };
  const allCallRecords = [];
  // Stays null unless at least one practice answered — a failed task fetch must
  // read as "Keine Aufgaben verfügbar", not as six zeroes.
  let taskCounts = null;
  let taskParts = 0;

  (parts || []).forEach((part) => {
    const stats = part?.callStats || {};
    callStats.total_calls += Number(stats.total_calls) || 0;
    callStats.completed_call_duration_seconds += Number(stats.completed_call_duration_seconds) || 0;
    callStats.escape_scope_total += Number(stats.escape_scope_total) || 0;
    addCounts(callStats.call_type_breakdown, stats.call_type_breakdown);
    addCounts(callStats.call_reasons_breakdown, stats.call_reasons_breakdown);
    addCounts(callStats.performance, stats.performance);
    if (Array.isArray(stats.escape_breakdown)) {
      callStats.escape_breakdown.push(...stats.escape_breakdown);
    }

    if (Array.isArray(part?.allCallRecords)) {
      allCallRecords.push(...part.allCallRecords);
    }

    if (part?.taskCounts) {
      taskCounts = taskCounts || {};
      addCounts(taskCounts, part.taskCounts);
      taskParts += 1;
    }
  });

  // taskParts is NOT derivable from taskCounts: a practice whose task fetch
  // failed still contributed call statistics, so the payload looks complete
  // while the six tiles are quietly short one practice. The caller needs both
  // numbers to say so.
  return { callStats, allCallRecords, taskCounts, taskParts };
};

